// src/LessonDetails.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { LanguageContext } from "./LanguageContext";
import "./css/LessonDetails.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useIsPaidUser } from "./hooks/useIsPaidUser";
import { stockTradingCourse, personalFinanceCourses, InvestparaBr } from "./CourseData";
import YouTubeEmbed from "./components/YouTubeEmbed";

const apiUrl = "https://qedeefqyh2.execute-api.us-west-2.amazonaws.com/prod";
const buildLessonApiUrl = ({ apiUrl, course, lang, module, lesson }) => {
  const courseKey = course === "stock-trading" ? "stock-trading-basics" : course;
  return `${apiUrl}/lesson/${encodeURIComponent(courseKey)}/${encodeURIComponent(lang)}/${encodeURIComponent(module)}/${encodeURIComponent(lesson)}`;
};


// ===== DEV BYPASS =====
//const isDev = process.env.NODE_ENV === "development";
const authEnabled = true; // disable auth/paid gating on localhost/dev

const LessonDetails = () => {
  const { module, lesson } = useParams(); // e.g. module='module0', lesson='lesson1'
  const history = useHistory();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const course = qs.get("course") || "personal-finance"; // default EN course key

  const { isAuthenticated, user, loginWithRedirect } = useAuth0();
  const { isPaidUser, isCheckingPaid } = useIsPaidUser(course);
  const { language, toggleLanguage, translations } = useContext(LanguageContext);

  // ===== When auth is disabled (dev), treat user as paid+authed for gating logic
  const effectiveIsAuthenticated = authEnabled ? isAuthenticated : true;
  const effectiveIsPaidUser = authEnabled ? isPaidUser : true;
  const effectiveIsCheckingPaid = authEnabled ? isCheckingPaid : false;

  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComingSoon, setShowComingSoon] = useState(false);

  const lessonNumber = parseInt(lesson.replace("lesson", ""), 10);
  const moduleNumber = parseInt(module.replace("module", ""), 10);

   // decide active course from the query string, not the module number
  const isPTCourse = course === "investindo-nos-eua";
  const isStockCourse = course === "stock-trading-basics";
  //const isPersonalEN = !isPTCourse && !isStockCourse;

  // ---- Active course modules (drives lessonIds, prev/next, video lookup)
   const activeModules = isStockCourse
   ? [{ module: stockTradingCourse.module, lessons: stockTradingCourse.lessons }]
   : (isPTCourse ? InvestparaBr : personalFinanceCourses);

  const moduleNumbers = activeModules.map((m) => m.module).sort((a, b) => a - b);
  const currentModuleObj =
    activeModules.find((m) => m.module === moduleNumber) || { lessons: [] };

  // lesson ids for the current module (per-module for PT; global for EN dataset)
  const lessonIds = currentModuleObj.lessons.map((l) => l.id);
  const firstLessonId = lessonIds[0] ?? null;
  const lastLessonId = lessonIds[lessonIds.length - 1] ?? null;

  // module navigation boundaries based on active course only
  const currIndex = moduleNumbers.indexOf(moduleNumber);
  const hasPrevModule = currIndex > 0;
  const hasNextModuleActive = currIndex >= 0 && currIndex < moduleNumbers.length - 1;
  const prevModuleNum = hasPrevModule ? moduleNumbers[currIndex - 1] : null;
  const nextModuleNum = hasNextModuleActive ? moduleNumbers[currIndex + 1] : null;

  // Use effective paid state for navigation gating
  const canAdvanceModule = effectiveIsPaidUser && hasNextModuleActive;

  useEffect(() => {
  if (authEnabled && !isAuthenticated) {
    // full path including query string, e.g. /lessons/module1/lesson2?lang=pt&course=investindo-nos-eua&free=1
    const fullPath = `${history.location.pathname}${history.location.search}`;

    // optional: keep your existing pattern from CoursesPage
    localStorage.setItem("auth_redirect_path", fullPath);

    loginWithRedirect({
      appState: { returnTo: fullPath },
    });
  }
}, [authEnabled, isAuthenticated, history, loginWithRedirect]);

  // Fetch lesson content
  useEffect(() => {
    if (authEnabled && !effectiveIsAuthenticated) return;

    setLoading(true);
    setError(null);
    setLessonData(null);

    const fetchLesson = async () => {
      try {
        // Back-end expects numeric lesson id in the path, and course in the query
        const url = buildLessonApiUrl({ apiUrl, course, lang: language, module, lesson: lessonNumber });
        const response = await fetch(url);
        if (!response.ok) throw new Error("Lesson not found");
        const data = await response.json();
        setLessonData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (lessonIds.includes(lessonNumber)) {
      fetchLesson();
    } else {
      setShowComingSoon(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, lesson, language, effectiveIsAuthenticated, lessonNumber, course]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lesson]);

  // ===== Guards (disabled in dev)
  if (authEnabled && !effectiveIsAuthenticated) {
    return <div>Redirecting to login…</div>;
  }

  const premiumModules = [1, 2, 3, 4, 5];
  const courseSlug = new URLSearchParams(search).get("course") || "personal-finance";
  const isPremiumModule =
    (courseSlug === "personal-finance" || courseSlug === "investindo-nos-eua") &&
    premiumModules.includes(moduleNumber);

  if (
    authEnabled &&
    isPremiumModule &&
    !effectiveIsCheckingPaid &&
    !effectiveIsPaidUser
  ) {
    alert(translations.fullAcess);
    history.push(`/premium?course=${encodeURIComponent(course)}`);
    return null;
  }

  // upgrade prompt for full access or out-of-bounds lesson
  if (showComingSoon) {
    return (
      <div className="page-wrapper">
        {/* Language Selector */}
        <button className="language-button" onClick={toggleLanguage}>
          {language === "en" ? "🇧🇷 Português" : "🇺🇸 English"}
        </button>
        <main>
          <section className="program-info">
            <h2>{translations.upgrademsg}</h2>
            <p>
              {effectiveIsPaidUser
                ? translations.congrats
                : translations.premiumMemberMsg}
            </p>
          </section>
          <section className="contact-form">
            <button
              className="nav-button"
              onClick={() =>
                history.push(effectiveIsPaidUser ? "/courses" : "/premium")
              }
            >
              {effectiveIsPaidUser ? "Back to Courses" : "Upgrade to Premium"}
            </button>
          </section>
        </main>
      </div>
    );
  }

  // loading or error states
  if (loading || (isPremiumModule && effectiveIsCheckingPaid)) {
    return <div>Loading...</div>;
  }
  if (error) return <div>Error: {error}</div>;
  if (!lessonData) return null;

  // "Lesson X" label as per-module index in the active course
  const displayLesson =
    (currentModuleObj.lessons.findIndex((l) => l.id === lessonNumber) >= 0
      ? currentModuleObj.lessons.findIndex((l) => l.id === lessonNumber) + 1
      : lessonNumber);

  // Prev/Next button visibility using active-course boundaries
  const showPrev =
    (firstLessonId != null && lessonNumber > firstLessonId) || hasPrevModule;
  const showNext =
    (lastLessonId != null && lessonNumber < lastLessonId) || canAdvanceModule;

  // Navigation handlers (preserve ?lang & ?course)
  const goToPrevLesson = () => {
    if (firstLessonId != null && lessonNumber > firstLessonId) {
      history.push(
        `/lessons/module${moduleNumber}/lesson${lessonNumber - 1}?lang=${language}&course=${encodeURIComponent(
          course
        )}`
      );
    } else if (hasPrevModule) {
      const pm = prevModuleNum;
      const prevObj = activeModules.find((m) => m.module === pm);
      const prevLast = prevObj?.lessons?.slice(-1)[0]?.id;
      if (prevLast != null) {
        history.push(
          `/lessons/module${pm}/lesson${prevLast}?lang=${language}&course=${encodeURIComponent(
            course
          )}`
        );
      }
    }
  };

  const goToNextLesson = () => {
    if (lastLessonId != null && lessonNumber < lastLessonId) {
      history.push(
        `/lessons/module${moduleNumber}/lesson${lessonNumber + 1}?lang=${language}&course=${encodeURIComponent(
          course
        )}`
      );
    } else if (canAdvanceModule) {
      const nm = nextModuleNum;
      const nextObj = activeModules.find((m) => m.module === nm);
      const nextFirst = nextObj?.lessons?.[0]?.id;
      if (nextFirst != null) {
        history.push(
          `/lessons/module${nm}/lesson${nextFirst}?lang=${language}&course=${encodeURIComponent(
            course
          )}`
        );
      }
    }
  };

  //Find lesson meta in the **active course** (for YouTube mapping)
  let currentLessonMeta = null;
  if (isStockCourse) {
    currentLessonMeta =
      stockTradingCourse.lessons.find((l) => l.id === lessonNumber) || null;
  } else if (isPTCourse) {
    const m = InvestparaBr.find((m) => m.module === moduleNumber);
    currentLessonMeta = m?.lessons.find((l) => l.id === lessonNumber) || null; // PT: usually no videoByLang
  } else {
    const m = personalFinanceCourses.find((m) => m.module === moduleNumber);
    currentLessonMeta = m?.lessons.find((l) => l.id === lessonNumber) || null;
  }

  // Pick the YouTube ID for current language; fallback to PT
  const youtubeId =
    currentLessonMeta?.videoByLang?.[language] ??
    currentLessonMeta?.videoByLang?.pt ??
    null;

  // Course display name for breadcrumbs
  const courseDisplayName = isStockCourse
    ? (language === "pt" ? "Stock Trading" : "Stock Trading")
    : isPTCourse
    ? (language === "pt" ? "Investindo nos EUA" : "Investing in the USA")
    : (language === "pt" ? "Finanças Pessoais" : "Personal Finance");

  // Course route for breadcrumb link
  const courseRoute = isStockCourse
    ? "/stock-trade"
    : isPTCourse
    ? "/investparabr"
    : "/personal-finance";

  return (
    <div className="lesson-page-wrapper">
      {/* Header Section */}
      <div className="lesson-header">
        <div className="lesson-header-content">
          {/* Breadcrumbs */}
          <nav className="lesson-breadcrumbs">
            <a href="/">{language === "pt" ? "Início" : "Home"}</a>
            <span className="breadcrumb-separator">›</span>
            <a href={courseRoute}>{courseDisplayName}</a>
            <span className="breadcrumb-separator">›</span>
            {!isStockCourse && (
              <>
                <span>{translations.module} {moduleNumber}</span>
                <span className="breadcrumb-separator">›</span>
              </>
            )}
            <span className="breadcrumb-current">{translations.lesson} {displayLesson}</span>
          </nav>

          {/* Module/Lesson Indicator */}
          <div className="lesson-indicator">
            {isStockCourse ? (
              <span className="lesson-badge">
                {translations.lesson} {displayLesson}
              </span>
            ) : (
              <span className="lesson-badge">
                {translations.module} {moduleNumber} – {translations.lesson} {displayLesson}
              </span>
            )}
            {!authEnabled && (
              <span className="dev-badge">Dev mode</span>
            )}
          </div>

          {/* Lesson Title */}
          <h1 className="lesson-header-title">{lessonData.title}</h1>
        </div>
      </div>

      {/* Content Section */}
      <div className="lesson-container">
        {/* VIDEO BLOCK */}
        {youtubeId && (
          <YouTubeEmbed
            youtubeId={youtubeId}
            title={`${lessonData.title} — ${translations.lesson} ${displayLesson}`}
          />
        )}

        <div className="lesson-content">
          <ReactMarkdown>{lessonData.content}</ReactMarkdown>
        </div>

        <div className="navigation-buttons">
          {showPrev && (
            <button className="nav-button" onClick={goToPrevLesson}>
              ← {translations.previous}
            </button>
          )}
          {showNext && (
            <button className="nav-button" onClick={goToNextLesson}>
              {translations.next} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonDetails;
